import React, { useState, useRef, useEffect } from "react";

const MultiSelectDropdown = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select options...",
  displayProperty = "label",
  valueProperty = "value",
  searchEnabled = true,
  className = "",
  style = {},
  disabled = false,
  label = "",
  labelClassName = "",
  dropdownClassName = "",
  optionClassName = "",
  searchPlaceholder = "Search...",
  noOptionsText = "No options found",
  paddingY = "py-2.5",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Update filtered options when options or searchTerm changes
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter((option) =>
        option[displayProperty]
          ?.toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [options, searchTerm, displayProperty]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchEnabled && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchEnabled]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm("");
      }
    }
  };

  const handleOptionClick = (option) => {
    const optionValue = option[valueProperty];
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchClick = (e) => {
    e.stopPropagation();
  };

  const removeSelected = (selectedValue) => {
    const newValue = value.filter((v) => v !== selectedValue);
    onChange(newValue);
  };

  // Get display text for selected values
  const getDisplayText = () => {
    if (!value || value.length === 0) return placeholder || "";
    if (value.length === 1) {
      const selectedOption = options.find(
        (option) => option[valueProperty] === value[0]
      );
      return selectedOption ? selectedOption[displayProperty] : value[0];
    }
    return `${value.length} selected`;
  };

  const isSelected = (option) => {
    return value.includes(option[valueProperty]);
  };

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 flex-wrap">
        {label && (
          <label
            className={`block text-sm font-bold text-[#1A212F] mb-2 ${labelClassName}`}
          >
            {label} :
          </label>
        )}
        {/* Dropdown Trigger */}
        <div
          ref={dropdownRef}
          className={`relative px-3 py-2 bg-[#F8F9FB] border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-[14px] font-medium cursor-pointer flex items-center justify-between min-w-[120px] ${
            disabled ? "bg-gray-50 text-gray-500 cursor-not-allowed" : ""
          } ${className}`}
          onClick={handleToggle}
          style={style}
        >
          <span className="text-[#1A212F66]">{placeholder}</span>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
              isOpen ? "transform rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>

          {/* Dropdown Menu */}
          {isOpen && (
            <div
              className={`absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden ${dropdownClassName}`}
            >
              {/* Search Input */}
              {searchEnabled && (
                <div className="p-2 border-b border-gray-300">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onClick={handleSearchClick}
                    placeholder={searchPlaceholder}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              )}

              {/* Options List */}
              <div className="max-h-48 overflow-y-auto">
                {filteredOptions.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500 text-center">
                    {noOptionsText}
                  </div>
                ) : (
                  filteredOptions.map((option, index) => (
                    <div
                      key={option[valueProperty] || index}
                      className={`px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors duration-150 flex items-center gap-2 ${
                        isSelected(option)
                          ? "bg-blue-50 text-blue-600"
                          : "text-gray-900"
                      } ${optionClassName}`}
                      onClick={() => handleOptionClick(option)}
                    >
                      <div
                        className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                          isSelected(option)
                            ? "bg-blue-600 border-blue-600"
                            : "border-gray-300"
                        }`}
                      >
                        {isSelected(option) && (
                          <svg
                            className="w-3 h-3 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                      {option[displayProperty]}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Selected Pills */}
        {value && value.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {value.map((selectedValue, index) => {
              const selectedOption = options.find(
                (option) => option[valueProperty] === selectedValue
              );
              const displayText = selectedOption
                ? selectedOption[displayProperty]
                : selectedValue;
              return (
                <span
                  key={selectedValue}
                  className="inline-flex items-center gap-3 px-2 py-1 border border-[#D6DBEA] bg-[#F8F9FB] text-[#1A212F] text-sm rounded-full"
                >
                  <span className="truncate max-w-20">{displayText}</span>
                  {!disabled && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeSelected(selectedValue);
                      }}
                      className="text-white w-4 h-4 rounded-full bg-[#006BF3] flex items-center justify-center"
                    >
                      ×
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelectDropdown;
